import { useMemo } from 'react';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { companyHub, useCompanyHub } from '@atlassian/global-shortcuts/company-hub';
import { jira, useJira } from '@atlassian/global-shortcuts/jira';
import { loom, useLoom } from '@atlassian/global-shortcuts/loom';
import { teams, useTeams } from '@atlassian/global-shortcuts/teams';

import { fg } from '@confluence/feature-gating';
import { isTeamsAppEnabled } from '@confluence/teams-app-config';

import { useFallbackUrls } from '../../useFallbackUrls';
import { CompanyHubItem } from '../../GlobalItems/CompanyHubItem/CompanyHubItem';

import { useLicensedProducts } from './useLicensedProducts';
import { useShortcutVisibilityOverrides } from './useShortcutVisibilityOverrides';

// Map shortcut configs to menu identifiers
export const useShortcutItemsConfig = (): Record<string, GlobalShortcutsItem> =>
	fg('navx-932-migrate-to-hooks-confluence')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useShortcutItemsConfigNew()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useShortcutItemsConfigOld();

const useShortcutItemsConfigNew = (): Record<string, GlobalShortcutsItem> => {
	const fallbackUrls = useFallbackUrls();
	const { hasLoom, hasJira } = useLicensedProducts();
	const teamsAppEnabled = isTeamsAppEnabled();
	const { isPeopleVisible, isCompanyHubVisible } = useShortcutVisibilityOverrides();

	const companyHubShortcutWithoutComponent = useCompanyHub(fallbackUrls['companyHubShortcut']);
	const jiraShortcut = useJira('');
	const loomShortcut = useLoom('');
	const teamsShortcut = useTeams(fallbackUrls['teamsShortcut']);

	return useMemo(() => {
		const companyHubShortcut = {
			...companyHubShortcutWithoutComponent,
			component: CompanyHubItem,
		};

		return {
			...(isCompanyHubVisible ? { companyHubShortcut } : {}),
			...(hasJira ? { jiraShortcut } : {}),
			...(hasLoom ? { loomShortcut } : {}),
			...(isPeopleVisible && teamsAppEnabled ? { teamsShortcut } : {}),
		};
	}, [
		companyHubShortcutWithoutComponent,
		hasJira,
		jiraShortcut,
		hasLoom,
		loomShortcut,
		teamsShortcut,
		teamsAppEnabled,
		isCompanyHubVisible,
		isPeopleVisible,
	]);
};

const useShortcutItemsConfigOld = (): Record<string, GlobalShortcutsItem> => {
	const fallbackUrls = useFallbackUrls();
	const { hasLoom, hasJira } = useLicensedProducts();
	const teamsAppEnabled = isTeamsAppEnabled();
	const { isPeopleVisible, isCompanyHubVisible } = useShortcutVisibilityOverrides();

	return useMemo(() => {
		return {
			...(isCompanyHubVisible
				? { companyHubShortcut: companyHubComponent(fallbackUrls['companyHubShortcut']) }
				: {}),
			...(hasJira ? { jiraShortcut: jira(() => '') } : {}),
			...(hasLoom ? { loomShortcut: loom(() => '') } : {}),
			...(isPeopleVisible && teamsAppEnabled
				? { teamsShortcut: teams(() => fallbackUrls['teamsShortcut']) }
				: {}),
		};
	}, [isCompanyHubVisible, fallbackUrls, hasJira, hasLoom, isPeopleVisible, teamsAppEnabled]);
};

function companyHubComponent(fallbackUrl: string): GlobalShortcutsItem {
	return {
		...companyHub(() => fallbackUrl),
		component: CompanyHubItem,
	};
}
